import { createStore, createEvent, createEffect, sample } from 'effector';
import { createTelegramApiClient } from '../utils/apiClient';
import { AuthenticatedRequest, createAuthenticatedRequest } from './types';
import { createSessionError } from './sessionStore';

// Типы
export interface TermsAgreementRequest {
  telegramId: string;
  agreed: boolean;
  timestamp: string;
}

export interface TermsAgreementResponse {
  success: boolean;
  message?: string;
}

export interface TermsError {
  message: string;
  isSessionExpired?: boolean;
  context?: string;
}

// События
export const setTermsAgreed = createEvent<boolean>();
export const setTermsVisible = createEvent<boolean>();
export const submitTermsAgreement = createEvent<boolean>();

// Эффекты
export const submitTermsAgreementFx = createEffect<AuthenticatedRequest<TermsAgreementRequest>, TermsAgreementResponse, TermsError>(async ({ data, initData }) => {
  console.log('submitTermsAgreementFx started with data:', data);
  
  try {
    const telegramClient = createTelegramApiClient(initData);
    const result = await telegramClient.post(`/campaign/terms/agree`, data, {
      loadingText: 'Отправляем согласие...'
    });
    
    console.log('submitTermsAgreementFx result:', result);
    return result;
  } catch (error: any) {
    console.log('submitTermsAgreementFx error:', error);
    
    // Используем централизованную обработку ошибок сессии
    const sessionError = createSessionError(error, 'terms-agreement');
    
    const termsError: TermsError = {
      message: sessionError.message,
      isSessionExpired: sessionError.isSessionExpired,
      context: sessionError.context
    };
    throw termsError;
  }
});

// Тип состояния
export interface TermsState {
  isAgreed: boolean;
  isVisible: boolean;
  isSubmitting: boolean;
  isSessionExpired: boolean;
}

// Store
export const termsStore = createStore<TermsState>({
  isAgreed: false,
  isVisible: true, // Показываем по умолчанию
  isSubmitting: false,
  isSessionExpired: false,
})
  .on(setTermsAgreed, (state, isAgreed) => ({ ...state, isAgreed }))
  .on(setTermsVisible, (state, isVisible) => ({ ...state, isVisible }))
  .on(submitTermsAgreementFx.pending, (state, isSubmitting) => ({ ...state, isSubmitting }))
  .on(submitTermsAgreementFx.doneData, (state, result) => {
    if (result.success) {
      return {
        ...state,
        isAgreed: true,
        isVisible: false, // Скрываем после успешного согласия
        isSubmitting: false
      };
    }
    return { ...state, isSubmitting: false };
  })
  .on(submitTermsAgreementFx.failData, (state, error) => {
    console.log('submitTermsAgreementFx failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    return {
      ...state,
      isSubmitting: false,
      isSessionExpired: error.isSessionExpired || false
    };
  });

// Логика
sample({
  clock: submitTermsAgreement,
  source: termsStore,
  filter: (state, agreed) => {
    const shouldSubmit = agreed && !state.isSubmitting && !state.isAgreed;
    console.log('submitTermsAgreement filter:', { 
      agreed, 
      isSubmitting: state.isSubmitting, 
      isAgreed: state.isAgreed, 
      shouldSubmit 
    });
    return shouldSubmit;
  },
  fn: (state, agreed) => {
    console.log('Preparing terms agreement request...');
    const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || '';
    return createAuthenticatedRequest({
      telegramId,
      agreed,
      timestamp: new Date().toISOString()
    }, window.Telegram?.WebApp?.initData || '');
  },
  target: submitTermsAgreementFx
});
